/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { isUndefined } from "lodash";
import {
  CostMappingAlgorithm,
  EnvCostShare,
  Provider,
  ResourceQuery,
} from "~/generated/graphql";
import { dateFormat } from "~/constants";
import { KeyValue } from "~/models";
import { ROUTES } from "~/routing/routes";
import {
  isCloudResource,
  isEmpty,
  isNotNil,
  isSomeEnum,
  Maybe,
  Typename,
} from "~/tools";
import { isGreaterThan } from "../helpers";
import { Resource } from "../types";

export function parseMetadata(meta: KeyValue) {
  const parsed = JSON.parse(meta.value);
  switch (typeof parsed) {
    case "number":
    case "boolean":
      return {
        label: meta.key,
        value: parsed.toString(),
      };
    case "object":
      if (parsed == null) {
        return {
          label: meta.key,
          value: "-",
        };
      }
      return {
        label: meta.key,
        value: parsed as Record<string, unknown>,
        popup: true,
      };
    default: {
      const path = encodeURIComponent(`"${meta.value.split('"').join("")}"`);
      return {
        label: meta.key,
        value: parsed as string | undefined,
        target:
          parsed.length > 2
            ? `${ROUTES.estateRecords.index}?query=${path}`
            : null,
        search: true,
      };
    }
  }
}

export function sortMetadata<T extends { key: string }>(a: T, b: T) {
  const A = a.key.toUpperCase();
  const B = b.key.toUpperCase();
  if (A > B) {
    return 1;
  } else if (A < B) {
    return -1;
  }
  return 0;
}

export function isPVInstance(resourceType: string): boolean {
  const PV_LIST = ["KUBERNETES/PERSISTENTVOLUME", "OPENSHIFT/PERSISTENTVOLUME"];
  return PV_LIST.includes(resourceType.toUpperCase());
}

export function isPVCInstance(resourceType: string): boolean {
  const PVC_LIST = [
    "KUBERNETES/PERSISTENTVOLUMECLAIM",
    "OPENSHIFT/PERSISTENTVOLUMECLAIM",
  ];
  return PVC_LIST.includes(resourceType.toUpperCase());
}

export function isStorageInstance(resourceType: string): boolean {
  const STORAGE_LIST = [
    "MICROSOFT.COMPUTE/DISKS",
    "COMPUTE.GOOGLEAPIS.COM/DISKS",
    "EC2/VOLUME",
    "IS/VOLUME",
  ];
  return STORAGE_LIST.includes(resourceType.toUpperCase());
}

export function isPodInstance(resourceType: string): boolean {
  const POD_NAME_LIST = ["POD/INSTANCE", "KUBERNETES/POD", "OPENSHIFT/POD"];
  return POD_NAME_LIST.includes(resourceType.toUpperCase());
}

export function isNodeInstance(resourceType: string): boolean {
  const NODE_NAME_LIST = [
    "NODE",
    "NODE/INSTANCE",
    "KUBERNETES/NODE",
    "OPENSHIFT/NODE",
  ];
  return NODE_NAME_LIST.includes(resourceType.toUpperCase());
}

export function isVMInstance(resourceType: string): boolean {
  const VM_RECORDS = [
    "ec2/instance",
    "microsoft.compute/virtualmachines",
    "microsoft.compute/virtualmachinescalesets",
    "compute.googleapis.com/instances",
    "containers-kubernetes/container_instance",
    "external/virtualmachine",
  ];
  return VM_RECORDS.includes(resourceType);
}

export function isSyntheticInstance(resourceType: string): boolean {
  return resourceType.toUpperCase().startsWith("SYNTHETIC");
}

export function byteSize(bytes: number): string {
  if (bytes === 0) {
    return "0";
  } else if (bytes < 1e3) {
    return `${bytes}B`;
  } else if (bytes < 1e6) {
    return `${bytes / 1e3}KB`;
  } else if (bytes < 1e9) {
    return `${bytes / 1e6}MB`;
  } else if (bytes < 1e12) {
    return `${bytes / 1e9}GB`;
  } else if (bytes < 1e15) {
    return `${bytes / 1e12}TB`;
  } else {
    return `${bytes / 1e15}PB`;
  }
}

export function getCloudResource<
  T extends {
    __typename?: Typename;
  }
>(resource: T): (T & { __typename: "CloudResource" }) | undefined {
  return isCloudResource(resource) ? resource : undefined;
}

type AttachmentSelections = {
  envCosts: EnvCostShare[];
  algorithm: CostMappingAlgorithm;
};

type AttachmentDisabledProps = {
  attachmentSelections: AttachmentSelections;
};

export const isAttachmentDisabled = (props: AttachmentDisabledProps) => {
  const { attachmentSelections } = props;

  const isCostMappingAlgorithm = isSomeEnum(CostMappingAlgorithm);

  if (
    isEmpty(attachmentSelections.envCosts) ||
    !isCostMappingAlgorithm(attachmentSelections.algorithm)
  ) {
    return true;
  }

  if (attachmentSelections.algorithm === CostMappingAlgorithm.Manual) {
    const allocatedCost = attachmentSelections.envCosts.reduce(
      (total: number, env: EnvCostShare) =>
        total + Number(env.splitValue || "0"),
      0
    );

    return allocatedCost !== 1;
  }

  return false;
};

export const isResourceCostSplitRules = (
  loadingCostSplitRules: boolean,
  resourceCostSplitRulesCount?: number
) => {
  if (loadingCostSplitRules) {
    return false;
  }

  return isGreaterThan(resourceCostSplitRulesCount ?? 0, 0);
};

export const getAttachmentSelections = (
  envCosts: EnvCostShare[],
  algorithm: CostMappingAlgorithm
): AttachmentSelections => {
  return {
    envCosts,
    algorithm,
  };
};

export const getProvider = (provider?: Provider) => {
  return provider ?? "";
};

export function getResources(nid: string, provider?: Provider): Resource[] {
  return [provider != null ? { nid, provider } : undefined].filter(isNotNil);
}

export function textWithFallback(input: Maybe<string>, fallback: string) {
  return input ?? fallback;
}

export function generateDataUrl(metadata: Record<string, unknown> | undefined) {
  return `data:text/json;charset=utf-8,${encodeURIComponent(
    JSON.stringify(metadata, undefined, 2)
  )}`;
}

export function reservationsDate(value?: string) {
  return !isUndefined(value)
    ? dayjs.utc(value).format(dateFormat.dayMonthYear)
    : "-";
}

type EstateRecord = ResourceQuery["estateRecord"];

export function getResourceVisabilityDates(estateRecord: EstateRecord) {
  if (isCloudResource(estateRecord) && isNotNil(estateRecord)) {
    return {
      creationDate: estateRecord?.createdAt ?? "",
      currentMonthLastSpendDate:
        estateRecord?.cost?.currentMonthLastSpendDate ?? "",
    };
  }

  return undefined;
}
